import { useCallback, useRef } from 'react';
import { Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTextField } from 'src/components/CustomTextField';
import { useScrollIntoViewConditionally } from 'src/lib/hooks';

import { useStyles } from './useStyles';

const FINISH_KEYS = [',', ';'];
const DEFAULT_OPTIONS = [];

export const AnnotationInput = ({
  className,
  value,
  onChange,
  onSave,
  error,
  disabled,
  autocompleteProps,
  placeholder,
  dataTestId,
}) => {
  const styles = useStyles();
  const refError = useRef();

  useScrollIntoViewConditionally({
    elementRef: refError,
    condition: Boolean(error),
  });

  const handleInputChange = useCallback(
    (event, newValue, action) => {
      if (action === 'reset') {
        if (autocompleteProps?.options?.includes(newValue)) {
          onChange(newValue);
        }
        return;
      }

      onChange(newValue);
    },
    [autocompleteProps, onChange]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (FINISH_KEYS.includes(event.key)) {
        event.preventDefault();
        onSave(event.target.value);
      } else if (event.key === 'Enter' && event.target.value !== '/') {
        onSave(event.target.value);
      }
    },
    [onSave]
  );

  return (
    <div className={cx(styles.root, className)}>
      <Autocomplete
        classes={{
          option: styles.option,
        }}
        options={DEFAULT_OPTIONS}
        inputValue={value}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        renderInput={(params) => (
          <CustomTextField
            className={styles.textField}
            placeholder={placeholder}
            disabled={disabled}
            error={Boolean(error)}
            variant="outlined"
            dataTestId={dataTestId}
            {...params}
            InputProps={{
              ...params.InputProps,
              className: styles.inputRoot,
            }}
          />
        )}
        freeSolo
        disableClearable
        {...autocompleteProps}
        disabled={disabled}
      />

      {error && (
        <Typography
          className={styles.error}
          ref={refError}
        >
          {error}
        </Typography>
      )}
    </div>
  );
};

AnnotationInput.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  autocompleteProps: PropTypes.shape(),
  placeholder: PropTypes.string,
  dataTestId: PropTypes.string,
};

AnnotationInput.defaultProps = {
  className: '',
  error: '',
  disabled: false,
  autocompleteProps: null,
  placeholder: 'Add a talking point and hit Enter',
  dataTestId: '',
};
